/**
 * The front door's animation: a punchboard, punching itself out.
 *
 * The name is the motif. A punchboard is the die-cut cardboard sheet a game's
 * counters arrive in, and the sheet here does what the app does, in the same
 * order: chits pop out of the board one at a time, tumble down to the table, and
 * land **face down** — five of them, one per player, none of them readable. Only
 * when the last one is down do they all flip at once. That is the product: rate
 * blind, reveal together, nobody anchored on whoever spoke first. Whoever
 * watches the hero for ten seconds has been told what Punchboard is without
 * reading a word of it.
 *
 * Everything here is deterministic and DOM-free apart from `drawSheet`, which is
 * the only function that touches a canvas context. That split is deliberate:
 * the timeline is the part that can be wrong, so the timeline is the part that
 * can be stepped and asserted without a browser.
 *
 * Cost discipline, in the model rather than bolted on afterwards:
 *   - `advance` clamps its delta, so a tab restored after an hour replays one
 *     frame, not two hundred thousand.
 *   - The loop is owned by `createTicker`, which cancels the pending frame on
 *     `stop`. Nothing else calls `requestAnimationFrame`.
 *   - `composeStatic` produces the whole story as one frame, for anyone who has
 *     asked their device to stop moving things.
 */

// ---------------------------------------------------------------------------
// Shape vocabulary
// ---------------------------------------------------------------------------

export type ShapeKind = "disc" | "hex" | "square" | "meeple" | "die";

const SHAPES: readonly ShapeKind[] = ["disc", "hex", "square", "meeple", "die", "disc", "hex"];

/** Seats at the table, and therefore chits in a reveal. */
export const SEATS = 5;

// Milliseconds. Read as a storyboard.
const PUNCH_EVERY = 620;
const FLIGHT = 900;
const SETTLE_HOLD = 800;
const REVEAL = 900;
const REVEAL_HOLD = 1900;
const CLEAR = 700;

/** One frame at 20fps. A backgrounded tab must not replay its whole absence. */
const MAX_STEP = 50;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface Cell {
  col: number;
  row: number;
  shape: ShapeKind;
  punched: boolean;
}

export interface Chit {
  shape: ShapeKind;
  /** Grid cell it was punched from, so the flight starts in the right hole. */
  col: number;
  row: number;
  /** Seat it is heading for, 0-based, left to right. */
  seat: number;
  /** Flight progress, 0 to 1. */
  t: number;
  spin: number;
  /** The rating it reveals: 0 to 1 along the scale. */
  value: number;
  side: "spice" | "slate";
}

export type Phase = "punching" | "settling" | "revealing" | "clearing";

export interface SheetState {
  cols: number;
  rows: number;
  cells: Cell[];
  /** Cell indices still to be punched, in the order they will go. */
  queue: number[];
  flying: Chit[];
  landed: Chit[];
  phase: Phase;
  /** Milliseconds spent in the current phase. */
  phaseAge: number;
  /** Flip progress of the landed row, 0 to 1. */
  reveal: number;
  /** Exit progress of the landed row, 0 to 1. */
  clear: number;
  /** Fresh-sheet slide-in, 0 (offscreen) to 1 (seated). */
  seat: number;
  sincePunch: number;
  elapsed: number;
  random: () => number;
}

/** Mulberry32 — small, fast, and the same sheet every time for a given seed. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffled(count: number, random: () => number): number[] {
  const order = Array.from({ length: count }, (_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const swap = order[i]!;
    order[i] = order[j]!;
    order[j] = swap;
  }
  return order;
}

export function createSheet(cols = 7, rows = 4, seed = 20260101): SheetState {
  const random = seededRandom(seed);
  const cells: Cell[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      cells.push({
        col,
        row,
        shape: SHAPES[Math.floor(random() * SHAPES.length)] ?? "disc",
        punched: false,
      });
    }
  }
  return {
    cols,
    rows,
    cells,
    queue: shuffled(cells.length, random),
    flying: [],
    landed: [],
    phase: "punching",
    phaseAge: 0,
    reveal: 0,
    clear: 0,
    seat: 1,
    sincePunch: PUNCH_EVERY,
    elapsed: 0,
    random,
  };
}

/**
 * Refill the board rather than allocate a new one.
 *
 * The sheet is never exhausted on screen: once fewer than a table's worth of
 * chits remain, the punched holes close up and a fresh sheet slides in. The
 * alternative — a board that empties and stays empty — turns the hero into a
 * thing that finishes, and a hero that finishes is a hero nobody looks at twice.
 */
function refill(state: SheetState): void {
  for (const cell of state.cells) {
    cell.punched = false;
    cell.shape = SHAPES[Math.floor(state.random() * SHAPES.length)] ?? "disc";
  }
  state.queue = shuffled(state.cells.length, state.random);
  state.seat = 0;
}

function punchOne(state: SheetState): void {
  const index = state.queue.pop();
  if (index === undefined) return;
  const cell = state.cells[index];
  if (!cell || cell.punched) return;
  cell.punched = true;
  state.flying.push({
    shape: cell.shape,
    col: cell.col,
    row: cell.row,
    seat: state.landed.length + state.flying.length,
    t: 0,
    // Half a turn either way: enough to read as tumbling, never so much that the
    // chit becomes a blur.
    spin: (state.random() * 2 - 1) * Math.PI,
    value: 0.15 + state.random() * 0.8,
    side: state.random() < 0.34 ? "slate" : "spice",
  });
}

/**
 * Step the timeline. Mutates and returns the same object — one sheet exists per
 * mounted hero and it is stepped sixty times a second, so this allocates nothing
 * per frame.
 */
export function advance(state: SheetState, deltaMs: number): SheetState {
  const dt = Math.max(0, Math.min(deltaMs, MAX_STEP));
  state.elapsed += dt;
  state.phaseAge += dt;
  if (state.seat < 1) state.seat = Math.min(1, state.seat + dt / CLEAR);

  for (const chit of state.flying) chit.t = Math.min(1, chit.t + dt / FLIGHT);
  // Landing is checked after the whole flock has moved so two chits that land on
  // the same frame both arrive, in seat order.
  for (let i = state.flying.length - 1; i >= 0; i--) {
    if (state.flying[i]!.t >= 1) state.landed.push(...state.flying.splice(i, 1));
  }
  state.landed.sort((a, b) => a.seat - b.seat);

  switch (state.phase) {
    case "punching": {
      state.sincePunch += dt;
      const airborne = state.flying.length + state.landed.length;
      if (state.sincePunch >= PUNCH_EVERY && airborne < SEATS && state.seat >= 1) {
        state.sincePunch = 0;
        punchOne(state);
      }
      if (state.landed.length >= SEATS) enter(state, "settling");
      break;
    }
    case "settling":
      // Every chit is down and face down. The pause is the point: this is the
      // moment at a real table where everyone has answered and nobody has looked.
      if (state.phaseAge >= SETTLE_HOLD) enter(state, "revealing");
      break;
    case "revealing":
      state.reveal = Math.min(1, state.phaseAge / REVEAL);
      if (state.phaseAge >= REVEAL + REVEAL_HOLD) enter(state, "clearing");
      break;
    case "clearing":
      state.clear = Math.min(1, state.phaseAge / CLEAR);
      if (state.clear >= 1) {
        state.landed = [];
        state.reveal = 0;
        state.clear = 0;
        if (state.queue.length < SEATS) refill(state);
        enter(state, "punching");
        state.sincePunch = PUNCH_EVERY;
      }
      break;
  }
  return state;
}

function enter(state: SheetState, phase: Phase): void {
  state.phase = phase;
  state.phaseAge = 0;
}

/**
 * One frame that tells the whole story, for `prefers-reduced-motion`.
 *
 * A still is what that preference asks for; a blank box is not. So the sheet is
 * stepped forward until it is holding a full, revealed table — part-punched
 * board, five chits down, all faces up — and that frame is what gets drawn, once.
 */
export function composeStatic(seed = 20260101): SheetState {
  const state = createSheet(7, 4, seed);
  // Bounded rather than `while (true)`: the timeline above always reaches a full
  // reveal well inside this, and a still frame is not worth a hang if it changes.
  for (let i = 0; i < 2000; i++) {
    advance(state, 16);
    if (state.phase === "revealing" && state.reveal >= 1) break;
  }
  return state;
}

// ---------------------------------------------------------------------------
// The loop
// ---------------------------------------------------------------------------

export interface TickerEnv {
  request: (cb: (time: number) => void) => number;
  cancel: (handle: number) => void;
  now: () => number;
}

export interface Ticker {
  start: () => void;
  stop: () => void;
  running: () => boolean;
}

/**
 * A rAF loop that can actually be turned off.
 *
 * `stop` cancels the frame that is already pending, so an unmounted hero cannot
 * paint one more time into a canvas that is gone, and a hidden tab costs nothing
 * at all. `start` while running is a no-op rather than a second loop — that is
 * the bug this exists to make unrepresentable, because two loops look identical
 * to one loop running at double speed until the battery says otherwise.
 */
export function createTicker(onFrame: (deltaMs: number) => void, env: TickerEnv): Ticker {
  let handle: number | null = null;
  let last = 0;

  const frame = (time: number): void => {
    handle = env.request(frame);
    const delta = time - last;
    last = time;
    onFrame(delta);
  };

  return {
    start(): void {
      if (handle !== null) return;
      last = env.now();
      handle = env.request(frame);
    },
    stop(): void {
      if (handle === null) return;
      env.cancel(handle);
      handle = null;
    },
    running(): boolean {
      return handle !== null;
    },
  };
}

/** The browser's own timing, as a `TickerEnv`. Never used by the tests. */
export function browserTicker(onFrame: (deltaMs: number) => void): Ticker {
  return createTicker(onFrame, {
    request: (cb) => requestAnimationFrame(cb),
    cancel: (h) => cancelAnimationFrame(h),
    now: () => performance.now(),
  });
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

export interface Palette {
  ground: string;
  groundDeep: string;
  surface: string;
  surfaceSunk: string;
  ink: string;
  inkSoft: string;
  inkFaint: string;
  rule: string;
  ruleStrong: string;
  spice: string;
  spiceHi: string;
  slate: string;
  slateHi: string;
  ochre: string;
  rust: string;
}

const FALLBACK: Palette = {
  ground: "#f2ede3",
  groundDeep: "#e6dfd1",
  surface: "#fbf8f2",
  surfaceSunk: "#eae3d6",
  ink: "#1a1712",
  inkSoft: "#5c5346",
  inkFaint: "#8b8172",
  rule: "#d3c9b6",
  ruleStrong: "#b7aa93",
  spice: "#c4661e",
  spiceHi: "#e0873c",
  slate: "#3f5a66",
  slateHi: "#5c7d8c",
  ochre: "#a8792c",
  rust: "#93331c",
};

const TOKENS: ReadonlyArray<[keyof Palette, string]> = [
  ["ground", "--pb-ground"],
  ["groundDeep", "--pb-ground-deep"],
  ["surface", "--pb-surface"],
  ["surfaceSunk", "--pb-surface-sunk"],
  ["ink", "--pb-ink"],
  ["inkSoft", "--pb-ink-soft"],
  ["inkFaint", "--pb-ink-faint"],
  ["rule", "--pb-rule"],
  ["ruleStrong", "--pb-rule-strong"],
  ["spice", "--pb-spice"],
  ["spiceHi", "--pb-spice-hi"],
  ["slate", "--pb-slate"],
  ["slateHi", "--pb-slate-hi"],
  ["ochre", "--pb-ochre"],
  ["rust", "--pb-rust"],
];

/**
 * Read the design tokens off the element the canvas lives in.
 *
 * Canvas cannot resolve a CSS variable, so the theme has to be handed to it.
 * Reading from the live element rather than hard-coding means the in-app theme
 * toggle, the OS preference and any future token change all reach the hero for
 * free — the hero simply asks what colour it is, roughly once a second.
 */
export function readPalette(el: Element | null): Palette {
  if (!el || typeof getComputedStyle !== "function") return FALLBACK;
  const style = getComputedStyle(el);
  const palette = { ...FALLBACK };
  for (const [key, token] of TOKENS) {
    const value = style.getPropertyValue(token).trim();
    if (value) palette[key] = value;
  }
  return palette;
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

export interface Viewport {
  width: number;
  height: number;
}

interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Geometry, kept out of the draw calls so the layout can be reasoned about. */
function layout(view: Viewport): { board: Frame; table: number; slot: number } {
  const pad = Math.max(10, Math.min(view.width, view.height) * 0.05);
  const boardH = view.height * 0.62;
  return {
    board: { x: pad, y: pad, w: view.width - pad * 2, h: boardH },
    table: view.height - pad - Math.min(46, view.height * 0.13),
    slot: (view.width - pad * 2) / SEATS,
  };
}

function cellCentre(state: SheetState, board: Frame, col: number, row: number): { x: number; y: number } {
  const inset = board.w * 0.06;
  const gw = board.w - inset * 2;
  const gh = board.h - inset * 2;
  return {
    x: board.x + inset + (gw / state.cols) * (col + 0.5),
    y: board.y + inset + (gh / state.rows) * (row + 0.5),
  };
}

function chitRadius(state: SheetState, board: Frame): number {
  const inset = board.w * 0.06;
  return Math.min((board.w - inset * 2) / state.cols, (board.h - inset * 2) / state.rows) * 0.36;
}

function traceShape(ctx: CanvasRenderingContext2D, shape: ShapeKind, r: number): void {
  ctx.beginPath();
  switch (shape) {
    case "disc":
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      break;
    case "hex":
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    case "square":
    case "die": {
      const s = r * 0.86;
      const k = r * 0.22;
      ctx.moveTo(-s + k, -s);
      ctx.arcTo(s, -s, s, s, k);
      ctx.arcTo(s, s, -s, s, k);
      ctx.arcTo(-s, s, -s, -s, k);
      ctx.arcTo(-s, -s, s, -s, k);
      ctx.closePath();
      break;
    }
    case "meeple": {
      // A silhouette, not a portrait: head, shoulders, a skirt to the base.
      const head = r * 0.34;
      ctx.arc(0, -r * 0.55, head, 0, Math.PI * 2);
      ctx.moveTo(-r * 0.86, r * 0.82);
      ctx.lineTo(-r * 0.5, -r * 0.06);
      ctx.lineTo(-r * 0.2, -r * 0.2);
      ctx.lineTo(r * 0.2, -r * 0.2);
      ctx.lineTo(r * 0.5, -r * 0.06);
      ctx.lineTo(r * 0.86, r * 0.82);
      ctx.closePath();
      break;
    }
  }
}

const PIPS: Record<number, ReadonlyArray<readonly [number, number]>> = {
  1: [[0, 0]],
  2: [
    [-0.4, -0.4],
    [0.4, 0.4],
  ],
  3: [
    [-0.42, -0.42],
    [0, 0],
    [0.42, 0.42],
  ],
  4: [
    [-0.4, -0.4],
    [0.4, -0.4],
    [-0.4, 0.4],
    [0.4, 0.4],
  ],
};

/** The printed face of an unpunched chit: enough ink to read as a component. */
function printMark(ctx: CanvasRenderingContext2D, chit: ShapeKind, r: number, palette: Palette, tint: string): void {
  ctx.save();
  if (chit === "die") {
    ctx.fillStyle = palette.inkSoft;
    const count = 3;
    for (const [px, py] of PIPS[count] ?? []) {
      ctx.beginPath();
      ctx.arc(px * r * 0.9, py * r * 0.9, Math.max(0.9, r * 0.11), 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (chit === "meeple") {
    ctx.fillStyle = tint;
    traceShape(ctx, "meeple", r * 0.62);
    ctx.fill();
  } else {
    ctx.strokeStyle = tint;
    ctx.lineWidth = Math.max(1, r * 0.13);
    traceShape(ctx, chit, r * 0.46);
    ctx.stroke();
  }
  ctx.restore();
}

/** Corner crosshair — the registration marks a printed sheet is trimmed against. */
function registration(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, colour: string): void {
  ctx.save();
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x - size, y);
  ctx.lineTo(x + size, y);
  ctx.moveTo(x, y - size);
  ctx.lineTo(x, y + size);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, size * 0.55, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function easeOut(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

/**
 * Paint one frame.
 *
 * The only function in this file that knows what a canvas is. `ctx` is expected
 * to be scaled for device pixel ratio by the caller, so every measurement here
 * is in CSS pixels.
 */
export function drawSheet(
  ctx: CanvasRenderingContext2D,
  state: SheetState,
  view: Viewport,
  palette: Palette,
): void {
  const { board, table, slot } = layout(view);
  const r = chitRadius(state, board);
  ctx.clearRect(0, 0, view.width, view.height);

  drawBoard(ctx, state, board, palette, r);
  drawTable(ctx, view, table, palette);
  drawFlying(ctx, state, board, view, table, slot, palette, r);
  drawLanded(ctx, state, view, table, slot, palette, r);
}

function drawBoard(
  ctx: CanvasRenderingContext2D,
  state: SheetState,
  board: Frame,
  palette: Palette,
  r: number,
): void {
  ctx.save();
  // A fresh sheet drops in from above; a seated one breathes, barely.
  const drop = (1 - easeOut(state.seat)) * (board.h + board.y);
  const sway = Math.sin(state.elapsed / 2600) * 2;
  ctx.translate(sway, -drop);
  ctx.globalAlpha = 0.35 + 0.65 * state.seat;

  ctx.fillStyle = palette.surface;
  ctx.strokeStyle = palette.ruleStrong;
  ctx.lineWidth = 1;
  const k = 4;
  ctx.beginPath();
  ctx.moveTo(board.x + k, board.y);
  ctx.arcTo(board.x + board.w, board.y, board.x + board.w, board.y + board.h, k);
  ctx.arcTo(board.x + board.w, board.y + board.h, board.x, board.y + board.h, k);
  ctx.arcTo(board.x, board.y + board.h, board.x, board.y, k);
  ctx.arcTo(board.x, board.y, board.x + board.w, board.y, k);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Print furniture: crosshairs on the trim corners, a colour bar top right.
  const m = Math.max(6, r * 0.42);
  for (const [cx, cy] of [
    [board.x + m * 1.4, board.y + m * 1.4],
    [board.x + board.w - m * 1.4, board.y + m * 1.4],
    [board.x + m * 1.4, board.y + board.h - m * 1.4],
    [board.x + board.w - m * 1.4, board.y + board.h - m * 1.4],
  ] as const) {
    registration(ctx, cx, cy, m * 0.62, palette.inkFaint);
  }
  const bars = [palette.spice, palette.slate, palette.ochre, palette.rust];
  const bw = Math.max(4, r * 0.34);
  bars.forEach((colour, i) => {
    ctx.fillStyle = colour;
    ctx.fillRect(board.x + board.w / 2 - (bars.length * bw) / 2 + i * bw, board.y + 5, bw - 1, bw * 0.5);
  });

  for (const cell of state.cells) {
    const { x, y } = cellCentre(state, board, cell.col, cell.row);
    ctx.save();
    ctx.translate(x, y);
    if (cell.punched) {
      // The hole left behind: the ground showing through, with the cut edge
      // catching light along the bottom the way stamped board does.
      ctx.fillStyle = palette.groundDeep;
      traceShape(ctx, cell.shape, r);
      ctx.fill();
      ctx.strokeStyle = palette.rule;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.save();
      ctx.clip();
      ctx.strokeStyle = palette.surfaceSunk;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, r * 0.24, r, Math.PI * 0.15, Math.PI * 0.85);
      ctx.stroke();
      ctx.restore();
    } else {
      ctx.fillStyle = palette.surfaceSunk;
      traceShape(ctx, cell.shape, r);
      ctx.fill();
      ctx.strokeStyle = palette.ruleStrong;
      ctx.lineWidth = 1;
      ctx.stroke();
      const tint = (cell.col + cell.row) % 3 === 0 ? palette.spice : palette.slate;
      printMark(ctx, cell.shape, r, palette, tint);
    }
    ctx.restore();
  }
  ctx.restore();
}

/** The table edge the chits land on: a dashed rule, the house's own furniture. */
function drawTable(ctx: CanvasRenderingContext2D, view: Viewport, table: number, palette: Palette): void {
  ctx.save();
  ctx.strokeStyle = palette.rule;
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 5]);
  ctx.beginPath();
  ctx.moveTo(0, table + 1);
  ctx.lineTo(view.width, table + 1);
  ctx.stroke();
  ctx.restore();
}

function seatX(view: Viewport, slot: number, index: number): number {
  const pad = (view.width - slot * SEATS) / 2;
  return pad + slot * (index + 0.5);
}

function drawFlying(
  ctx: CanvasRenderingContext2D,
  state: SheetState,
  board: Frame,
  view: Viewport,
  table: number,
  slot: number,
  palette: Palette,
  r: number,
): void {
  for (const chit of state.flying) {
    const from = cellCentre(state, board, chit.col, chit.row);
    const to = { x: seatX(view, slot, chit.seat), y: table - r * 0.9 };
    const t = chit.t;
    const x = from.x + (to.x - from.x) * t;
    // An arc, not a line: it pops toward the viewer before it falls.
    const y = from.y + (to.y - from.y) * easeOut(t) - Math.sin(Math.PI * t) * r * 2.4;
    const pop = 1 + Math.sin(Math.PI * t) * 0.28;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(chit.spin * t);
    ctx.scale(pop, pop);
    ctx.fillStyle = palette.surfaceSunk;
    ctx.strokeStyle = palette.ruleStrong;
    ctx.lineWidth = 1;
    traceShape(ctx, chit.shape, r);
    ctx.fill();
    ctx.stroke();
    printMark(ctx, chit.shape, r, palette, chit.side === "slate" ? palette.slate : palette.spice);
    ctx.restore();
  }
}

/**
 * The five seats. Face down until the reveal, then all of them at once.
 *
 * The flip is a horizontal squash through zero width — the same move a real card
 * makes — and the face only swaps at the halfway point, so nothing is legible a
 * frame early. That is the whole promise of the app rendered as one gesture.
 */
function drawLanded(
  ctx: CanvasRenderingContext2D,
  state: SheetState,
  view: Viewport,
  table: number,
  slot: number,
  palette: Palette,
  r: number,
): void {
  for (const chit of state.landed) {
    const x = seatX(view, slot, chit.seat);
    const y = table - r * 0.9;
    // The row exits together, sliding down off the table.
    const exit = easeOut(state.clear);
    const flip = state.reveal;
    const squash = Math.abs(Math.cos(Math.PI * Math.min(1, flip)));
    const faceUp = flip > 0.5;

    ctx.save();
    ctx.globalAlpha = 1 - exit;
    ctx.translate(x, y + exit * r * 4);
    ctx.scale(Math.max(0.04, squash), 1);

    if (faceUp) {
      const body = chit.side === "slate" ? palette.slate : palette.spice;
      const glint = chit.side === "slate" ? palette.slateHi : palette.spiceHi;
      ctx.fillStyle = palette.surface;
      traceShape(ctx, "disc", r);
      ctx.fill();
      ctx.strokeStyle = body;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // The gem, seated: the same object the rating control uses.
      ctx.fillStyle = glint;
      ctx.beginPath();
      ctx.arc(0, -r * 0.06, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = body;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.42, 0, Math.PI * 2);
      ctx.fill();
      // A stop on the scale, not a number: the value as a notch on the rim.
      const angle = Math.PI * (0.75 + 1.5 * chit.value);
      ctx.strokeStyle = body;
      ctx.lineWidth = Math.max(2, r * 0.16);
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.78, Math.PI * 0.75, angle);
      ctx.stroke();
    } else {
      ctx.fillStyle = palette.surfaceSunk;
      traceShape(ctx, "disc", r);
      ctx.fill();
      ctx.strokeStyle = palette.ruleStrong;
      ctx.lineWidth = 1;
      ctx.stroke();
      // Hatching: the same "concealed" texture the rating rail wears.
      ctx.save();
      ctx.clip();
      ctx.strokeStyle = palette.rule;
      ctx.lineWidth = 1;
      for (let o = -r * 2; o < r * 2; o += 4) {
        ctx.beginPath();
        ctx.moveTo(o, -r);
        ctx.lineTo(o + r * 2, r);
        ctx.stroke();
      }
      ctx.restore();
    }
    ctx.restore();
  }
}
